/**
 * AssessmentInProgressBanner — pinned to the top of /coach showing any
 * unsent draft assessment sessions. One tap to resume.
 *
 * Polls /coach/assessment-sessions?status=draft on mount; refreshes when
 * the route is re-entered. Hides itself if there are no drafts.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Activity, ChevronRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AssessmentInProgressBanner() {
    const [drafts, setDrafts] = useState([]);
    const navigate = useNavigate();

    useEffect(() => {
        let cancelled = false;
        axios.get(`${API}/coach/assessment-sessions?status=draft`, { withCredentials: true })
            .then((r) => { if (!cancelled) setDrafts(r.data || []); })
            .catch(() => {});
        return () => { cancelled = true; };
    }, []);

    if (!drafts.length) return null;

    return (
        <div data-testid="assessment-in-progress-banner" className="space-y-2">
            {drafts.map((d) => {
                const names = (d.participants || []).map((p) => p.name.split(" ")[0]).filter(Boolean);
                const headline = names.length === 0
                    ? "Empty draft"
                    : names.length === 1
                        ? names[0]
                        : `${names[0]} +${names.length - 1}`;
                const when = d.updated_at
                    ? new Date(d.updated_at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })
                    : "";
                return (
                    <button
                        key={d.session_id}
                        type="button"
                        data-testid={`resume-${d.session_id}`}
                        onClick={() => navigate(`/coach/assessment/${d.session_id}`)}
                        className="w-full flex items-center gap-3 border border-brand-red bg-brand-red/10 hover:bg-brand-red/15 transition-colors rounded-xl px-4 py-3 text-left"
                    >
                        <div className="w-9 h-9 rounded-full bg-brand-red/20 border border-brand-red/40 flex items-center justify-center shrink-0">
                            <Activity className="w-4 h-4 text-brand-red" />
                        </div>
                        <div className="min-w-0 flex-1">
                            <div className="text-[10px] tracking-[0.25em] uppercase text-brand-red">Assessment in progress</div>
                            <div className="text-sm font-semibold text-white truncate">
                                {headline}
                                <span className="text-zinc-300 font-normal ml-2">· {d.started_by_name || "—"}</span>
                            </div>
                        </div>
                        <div className="text-[10px] tracking-[0.2em] uppercase text-zinc-400 hidden sm:block">{when}</div>
                        <ChevronRight className="w-4 h-4 text-brand-red shrink-0" />
                    </button>
                );
            })}
        </div>
    );
}
